import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import apiService from '../../../services/api';
import { AIMood, MoodAnalytics } from './types';

export function useMoodsData() {
  const dispatch = useDispatch<AppDispatch>();

  const [moods, setMoods] = useState<AIMood[]>([]);
  const [analytics, setAnalytics] = useState<MoodAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMoods = async () => {
    try {
      const response = await apiService.getAIMoods();
      const moodsData: AIMood[] = (response || []).map((mood: any) => ({
        id: mood.id,
        mood_key: mood.mood_key || '',
        display_name: mood.display_name || mood.name || 'Untitled Mood',
        description: mood.description || '',
        emotional_keywords: mood.emotional_keywords || [],
        color_primary: mood.color_primary || '#6B8CAE',
        color_secondary: mood.color_secondary || '#E6F0FF',
        icon_name: mood.icon_name || 'mood',
        grok_prompt: mood.grok_prompt || mood.ai_prompt_template || '',
        writing_style_hints: mood.writing_style_hints || {},
        illustration_style_hints: mood.illustration_style_hints || {},
        is_active: mood.is_active !== false,
        sort_order: mood.sort_order ?? 99,
        created_at: mood.created_at || new Date().toISOString(),
        updated_at: mood.updated_at || new Date().toISOString(),
      }));
      setMoods(moodsData);
    } catch (error) {
      console.error('Failed to load moods:', error);
      setMoods([]);
      dispatch(addNotification({ type: 'error', message: 'Failed to load moods' }));
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiService.getAIMoodsAnalytics();
      const data = response?.data || response;
      setAnalytics({
        totalMoods: data?.totalMoods || moods.length,
        totalUsages: data?.totalUsages || 0,
        totalUsersWithPreferences: data?.totalUsersWithPreferences || 0,
        lastUpdated: data?.lastUpdated || new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to load moods analytics:', error);
      setAnalytics({
        totalMoods: moods.length,
        totalUsages: 0,
        totalUsersWithPreferences: 0,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadMoods();
    setLoading(false);
    dispatch(addNotification({ type: 'success', message: 'AI Moods data refreshed' }));
  };

  const handleToggleStatus = async (id: number, currentActive: boolean) => {
    const nextActive = !currentActive;
    try {
      await apiService.toggleAIMood(id, nextActive);
      dispatch(addNotification({
        type: 'success',
        message: nextActive ? 'Mood activated' : 'Mood deactivated',
      }));
      await loadMoods();
    } catch (error: any) {
      const msg = error?.message || String(error);
      const is409 = msg.includes('409') || /max.*active|active.*max/i.test(msg);
      dispatch(addNotification({
        type: 'error',
        message: is409 ? 'Maximum 5 active moods allowed. Deactivate one first.' : msg,
      }));
    }
  };

  const handleDeleteMood = async (moodId: number) => {
    if (!window.confirm('Are you sure you want to delete this mood?')) return;
    try {
      await apiService.deleteAIMood(moodId);
      dispatch(addNotification({ type: 'success', message: 'Mood deleted successfully' }));
      await loadMoods();
    } catch (error) {
      console.error('Delete error:', error);
      dispatch(addNotification({ type: 'error', message: 'Failed to delete mood' }));
    }
  };

  const handleSubmitMood = async (
    data: any,
    editingMood: AIMood | null,
    onSuccess: () => void,
  ) => {
    try {
      if (editingMood) {
        await apiService.updateAIMood(editingMood.id, data);
        dispatch(addNotification({ type: 'success', message: 'Mood updated successfully' }));
      } else {
        await apiService.createAIMood(data);
        dispatch(addNotification({ type: 'success', message: 'Mood created successfully' }));
      }
      onSuccess();
      await loadMoods();
    } catch (error) {
      console.error('Form submission error:', error);
      dispatch(addNotification({
        type: 'warning',
        message: `Mood ${editingMood ? 'update' : 'creation'} failed`,
      }));
      onSuccess();
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadMoods();
      setLoading(false);
    };
    loadData();
  }, []);

  // Load analytics when moods change
  useEffect(() => {
    if (moods.length > 0) {
      loadAnalytics();
    }
  }, [moods]);

  return {
    moods,
    analytics,
    loading,
    handleRefresh,
    handleToggleStatus,
    handleDeleteMood,
    handleSubmitMood,
  };
}
